import { z } from "zod";
import { listExistingPatientSchema, patientTypeSchema } from "./patients";
import { medicalCompetenceSchema } from "./shifts";
import { timestampSchema } from "./date-and-time";
import { categorySchema } from "./activities";

const invoicingCodeTypeOptions = ["procedureCode", "productCode"] as const;
export const invoicingCodeTypeOptionsSchema = z.enum(invoicingCodeTypeOptions);
export type IInvoicingCodeType = z.infer<typeof invoicingCodeTypeOptionsSchema>;

export const invoicingCodeSchema = z.object({
  id: z.string(),
  name: z.string(),
  code: z.string(),
  type: invoicingCodeTypeOptionsSchema,
  patientType: patientTypeSchema,
});
export type IInvoicingCode = z.infer<typeof invoicingCodeSchema>;

export const invoiceStatusSchema = z.enum([
  "awaitingApproval",
  "pending",
  "error",
  "completed",
  "notExecuted",
]);
export type IInvoiceStatus = z.infer<typeof invoiceStatusSchema>;

export type IOptionalInvoicingCodes = {
  procedureCodes?: string[];
  productCodes?: string[];
};

export const activityInvoicingCodesSchema = z.object({
  procedureCodes: z.array(invoicingCodeSchema),
  productCodes: z.array(invoicingCodeSchema),
});

export const occurrenceInvoicingCodesSchema = z.object({
  procedureCodes: z.array(invoicingCodeSchema),
  productCodes: z.array(invoicingCodeSchema),
});

// A row inside a group is billed to the group's patient, so only the group is
// enriched and the row keeps its id.
export const groupedInvoicingActivityOccurrenceSchema = z.object({
  patientId: z.uuid(),
  title: z.string(),
  activityId: z.uuid(),
  occurrenceId: z.string(),
  shiftIds: z.array(z.number()),
  requiredCompetences: z.array(medicalCompetenceSchema),
  finishedAt: timestampSchema,
  procedureCodes: z.array(invoicingCodeSchema),
  productCodes: z.array(invoicingCodeSchema),
  duration: z.number(),
  status: invoiceStatusSchema,
  activityType: categorySchema,
  error: z.string().nullish(),
});

export const invoicingActivityOccurrenceSchema =
  groupedInvoicingActivityOccurrenceSchema
    .omit({ patientId: true })
    .extend({ patient: listExistingPatientSchema });

export const invoicingGroupSchema = z.object({
  patient: listExistingPatientSchema,
  groupId: z.string(),
  occurrences: z.array(groupedInvoicingActivityOccurrenceSchema),
});

const invoicingActivityOccurrenceOrGroupSchema = z.union([
  invoicingActivityOccurrenceSchema,
  invoicingGroupSchema,
]);
export type IInvoicingActivityOccurrenceOrGroup = z.infer<
  typeof invoicingActivityOccurrenceOrGroupSchema
>;
